import React, { useState, useEffect } from 'react';
import axios from 'axios';
import './MyEarnings.css';

const MyEarnings = ({ userData }) => {
  const [activeTab, setActiveTab] = useState('summary');
  const [isLoading, setIsLoading] = useState(true);
  const [earningsData, setEarningsData] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [error, setError] = useState(null);

  const fetchEarningsData = async () => {
    try {
      setIsLoading(true);
      setError(null);
      // local date (toISOString is UTC: just after midnight on the 1st it gave the month before)
      const dateString = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(currentDate.getDate()).padStart(2, '0')}`;
      const response = await axios.get(
        `${process.env.REACT_APP_API_BASE_URL}/api/teacher/earnings/${userData.id}/${dateString}`
      );
      setEarningsData(response.data);
    } catch (err) {
      setError('Failed to fetch earnings data. Please try again later.');
      console.error('Error fetching earnings:', err);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (userData) {
      fetchEarningsData();
    }
  }, [userData, currentDate]);

  const handlePreviousMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() - 1);
    setCurrentDate(newDate);
  };

  const handleNextMonth = () => {
    const newDate = new Date(currentDate);
    newDate.setMonth(newDate.getMonth() + 1);
    const now = new Date();
    if (newDate.getMonth() <= now.getMonth() || newDate.getFullYear() < now.getFullYear()) {
      setCurrentDate(newDate);
    }
  };

  const formatCurrency = (amount) => {
    return amount.toLocaleString('en-US', { style: 'currency', currency: 'USD' });
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      weekday: 'long',
      month: 'long',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getMonthYearString = (date) => {
    const months = [
      'January', 'February', 'March', 'April', 'May', 'June',
      'July', 'August', 'September', 'October', 'November', 'December'
    ];
    return `${months[date.getMonth()]} ${date.getFullYear()}`;
  };

  const getPaymentStatus = (halfpay) => {
    switch(halfpay) {
      case 0: return 'Full Pay';
      case 1: return 'Half Pay';
      case 2: return 'No Pay';
      default: return 'Unknown';
    }
  };

  if (isLoading || !userData) {
    return (
      <div className="my-earnings">
        <div className="loading-container">
          <div className="loading-spinner"></div>
          <p>Loading your Earnings...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="my-earnings">
      <header className="my-classes__header">
        <div className="my-classes__header-content">
          <h1>
            Welcome back, <span>{userData.fullName}</span>
          </h1>
          <p className="my-classes__subheader">This is your Earnings Overview <span>for {getMonthYearString(currentDate)}</span></p>
        </div>
        
      </header>


      <header className="page-header">
        <div className="header-content">
          
          <div className="month-navigation">
            <button onClick={handlePreviousMonth} className="nav-button">
              &lt; Prev
            </button>
            <span className="current-month">{getMonthYearString(currentDate)}</span>
            <button 
              onClick={handleNextMonth} 
              className="nav-button"
              disabled={
                currentDate.getMonth() === new Date().getMonth() && 
                currentDate.getFullYear() === new Date().getFullYear()
              }
            >
              Next &gt;
            </button>
          </div>
        </div>
      </header>

      <div className="tabs-container">
        <div className="tabs">
          <button
            className={`tab ${activeTab === 'summary' ? 'active' : ''}`}
            onClick={() => setActiveTab('summary')}
          >
            Summary
          </button>
          <button
            className={`tab ${activeTab === 'paid' ? 'active' : ''}`}
            onClick={() => setActiveTab('paid')}
          >
            Paid Sessions
          </button>
          <button
            className={`tab ${activeTab === 'demo' ? 'active' : ''}`}
            onClick={() => setActiveTab('demo')}
          >
            Demo Sessions
          </button>
          <button
            className={`tab ${activeTab === 'incentive' ? 'active' : ''}`}
            onClick={() => setActiveTab('incentive')}
          >
            Incentives
          </button>
          <button
            className={`tab ${activeTab === 'penalties' ? 'active' : ''}`}
            onClick={() => setActiveTab('penalties')}
          >
            Penalties
          </button>
        </div>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="earnings-content">
        {activeTab === 'summary' && earningsData && (
          <div className="summary-section">
            <div className="earnings-table-container">
              <table className="earnings-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Count</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  <tr>
                    <td>Demo Sessions</td>
                    <td>{earningsData.demoCount || 0}</td>
                    <td className="amount">{formatCurrency(earningsData.demoPayTotal || 0)}</td>
                  </tr>
                  <tr>
                    <td>Paid Classes (Full Pay)</td>
                    <td>{earningsData.fullPayCount}</td>
                    <td className="amount">{formatCurrency(earningsData.fullPayTotal)}</td>
                  </tr>
                  <tr>
                    <td>Paid Classes (Half Pay)</td>
                    <td>{earningsData.halfPayCount}</td>
                    <td className="amount">{formatCurrency(earningsData.halfPayTotal)}</td>
                  </tr>
                  
                  <tr>
                    <td>Incentives</td>
                    <td>-</td>
                    <td className="amount">{formatCurrency(earningsData.incentive)}</td>
                  </tr>
                  {(earningsData.packageBonusEnabled || Number(earningsData.packageBonus) > 0) && (
                    <tr>
                      <td>Package bonuses</td>
                      <td>{earningsData.packageBonusCount || 0}</td>
                      <td className="amount">{formatCurrency(earningsData.packageBonus || 0)}</td>
                    </tr>
                  )}
                  <tr>
                    <td>Arrears</td>
                    <td>-</td>
                    <td className="amount">{formatCurrency(earningsData.arrears)}</td>
                  </tr>
                  <tr className="penalty-row">
                    <td>Penalties</td>
                    <td>-</td>
                    <td className="amount negative">{formatCurrency(earningsData.penalties)}</td>
                  </tr>
                  <tr className="total-row">
                    <td colSpan="2"><strong>Total Earnings</strong></td>
                    <td className="amount total">{formatCurrency(earningsData.totalPay)}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'paid' && earningsData && (
          <div className="paid-details-section">
            <div className="details-table-container">
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Date & Time</th>
                    <th>Rate</th>
                    <th>Status</th>
                    <th>Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsData.paidDetails && earningsData.paidDetails.length > 0 ? (
                    earningsData.paidDetails.map((detail, index) => (
                      <tr key={index}>
                        <td>{detail.fullname}</td>
                        <td>{formatDate(detail.ClassDateTime)}</td>
                        <td>{formatCurrency(detail.SessionRate)}</td>
                        <td>
                          <span className={`status-badge ${detail.fullpay === 0 ? 'full-pay' : detail.fullpay === 1 ? 'half-pay' : 'no-pay'}`}>
                            {getPaymentStatus(detail.fullpay)}
                          </span>
                        </td>
                        <td className="amount">
                          {detail.fullpay === 0 ? formatCurrency(detail.SessionRate) : 
                           detail.fullpay === 1 ? formatCurrency(detail.SessionRate / 2) : 
                           formatCurrency(0)}
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="5" className="no-data">No classes recorded this month</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'demo' && earningsData && (
          <div className="demo-details-section">
            <div className="details-table-container">
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Student</th>
                    <th>Date & Time</th>
                    <th>Rate</th>
                    <th>Status</th>
                    <th>Earnings</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsData.demoDetails && earningsData.demoDetails.length > 0 ? (
                    earningsData.demoDetails.map((detail, index) => (
                      <tr key={index}>
                        <td>{detail.fullname}</td>
                        <td>{formatDate(detail.ClassDateTime)}</td>
                        <td>$1.00</td>
                        <td>
                          <span className="status-badge full-pay">Demo Completed</span>
                        </td>
                        <td className="amount">
                          $1.00
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" className="no-data">No demo sessions this month</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'incentive' && earningsData && earningsData.incentiveData && (
          <div className="incentive-section">
            <div className="details-table-container">
              <table className="details-table">
                <thead>
                  <tr>
                    <th>Type</th>
                    <th>Title</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsData.incentiveData.filter((item) => item.Amount > 0).map((item) => (
                    <tr key={item.Id}>
                      <td>{item.Amount < 0 ? 'Penalty' : 'Incentive Pay'}</td>
                      <td>{item.Title}</td>
                      <td className={item.Amount < 0 ? 'amount negative' : 'amount'}>
                        {formatCurrency(item.Amount)}
                      </td>
                    </tr>
                  ))}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td></td>
                    <td className="amount total">
                    {formatCurrency(
                        earningsData.incentiveData
                          .filter((item) => item.Amount > 0)
                          .reduce((total, item) => total + item.Amount, 0)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {activeTab === 'penalties' && earningsData && earningsData.incentiveData && (
          <div className="penalties-section">
            <div className="penalties-table-container">
              <table className="penalties-table">
                <thead>
                  <tr>
                    <th>Reason</th>
                    <th>Title</th>
                    <th>Amount</th>
                  </tr>
                </thead>
                <tbody>
                  {earningsData.incentiveData
                    .filter((item) => item.Amount < 0)
                    .map((item) => (
                      <tr key={item.Id}>
                        <td>Penalty</td>
                        <td>{item.Title}</td>
                        <td className="amount negative">{formatCurrency(item.Amount)}</td>
                      </tr>
                    ))}
                  {earningsData.incentiveData.filter((item) => item.Amount < 0).length === 0 && (
                    <tr>
                      <td>No Penalty</td>
                      <td>-</td>
                      <td className="amount negative">-$0.00</td>
                    </tr>
                  )}
                  <tr className="total-row">
                    <td><strong>Total</strong></td>
                    <td></td>
                    <td className="amount total">
                      {formatCurrency(
                        earningsData.incentiveData
                          .filter((item) => item.Amount < 0)
                          .reduce((total, item) => total + item.Amount, 0)
                      )}
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}


      </div>
    </div>
  );
};

export default MyEarnings;